import { OrderId } from '../value-objects/OrderId';
import { Email } from '../value-objects/Email';
import { PhoneNumber } from '../value-objects/PhoneNumber';
import { Address } from '../value-objects/Address';
import { Money } from '../value-objects/Money';
import { OrderItem } from './OrderItem';

export type OrderStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled';
export type PaymentStatus = 'pending' | 'succeeded' | 'failed' | 'canceled';

export interface DeliveryInfo {
  fullName: string;
  phoneNumber: PhoneNumber;
  email: Email;
  contactMethod: string;
  address: Address | null;
  deliveryDate: Date;
  deliveryTime: string;
}

export interface DietaryRequirements {
  vegetarian: boolean;
  vegan: boolean;
  glutenFree: boolean;
  dairyFree: boolean;
  nuts: boolean;
  shellfish: boolean;
  eggs: boolean;
  soy: boolean;
  additionalComments: string;
}

export class Order {
  private constructor(
    private readonly _id: OrderId,
    private readonly _items: OrderItem[],
    private readonly _deliveryInfo: DeliveryInfo,
    private readonly _dietaryRequirements: DietaryRequirements,
    private _status: OrderStatus,
    private readonly _createdAt: Date,
    private _isPaid: boolean = false,
    private _paymentStatus: PaymentStatus = 'pending',
  ) {}

  static create(items: OrderItem[], deliveryInfo: DeliveryInfo, dietaryRequirements: DietaryRequirements): Order {
    if (items.length === 0) {
      throw new Error('Order must contain at least one item');
    }

    return new Order(
      OrderId.generateNewOrderId(),
      [...items],
      deliveryInfo,
      dietaryRequirements,
      'pending',
      new Date(),
    );
  }

  static restore(
    id: OrderId,
    items: OrderItem[],
    deliveryInfo: DeliveryInfo,
    dietaryRequirements: DietaryRequirements,
    status: OrderStatus,
    createdAt: Date,
    isPaid: boolean = false,
    paymentStatus: PaymentStatus = 'pending',
  ): Order {
    return new Order(id, items, deliveryInfo, dietaryRequirements, status, createdAt, isPaid, paymentStatus);
  }

  getId(): OrderId {
    return this._id;
  }

  getItems(): OrderItem[] {
    return [...this._items];
  }

  getDeliveryInfo(): DeliveryInfo {
    return this._deliveryInfo;
  }

  getDietaryRequirements(): DietaryRequirements {
    return this._dietaryRequirements;
  }

  getStatus(): OrderStatus {
    return this._status;
  }

  getCreatedAt(): Date {
    return this._createdAt;
  }

  isPaid(): boolean {
    return this._isPaid;
  }

  getPaymentStatus(): PaymentStatus {
    return this._paymentStatus;
  }

  calculateTotal(): Money {
    return this._items.reduce((total, item) => total.addMoney(item.getSubtotal()), Money.createZeroAmount());
  }

  getTotalItemCount(): number {
    return this._items.reduce((count, item) => count + item.getQuantity(), 0);
  }

  confirm(): void {
    if (this._status !== 'pending') {
      throw new Error(`Cannot confirm order with status: ${this._status}`);
    }
    this._status = 'confirmed';
  }

  complete(): void {
    if (this._status !== 'confirmed') {
      throw new Error(`Cannot complete order with status: ${this._status}`);
    }
    this._status = 'completed';
  }

  cancel(): void {
    if (this._status === 'completed') {
      throw new Error('Cannot cancel completed order');
    }
    this._status = 'cancelled';
  }

  markAsPaid(): void {
    this._isPaid = true;
    this._paymentStatus = 'succeeded';
  }

  markPaymentFailed(): void {
    this._paymentStatus = 'failed';
  }

  hasValidDeliveryInfo(): boolean {
    const info = this._deliveryInfo;
    return !!(info.fullName && info.phoneNumber && info.email && info.deliveryDate && info.deliveryTime);
  }

  equals(other: Order): boolean {
    return this._id.isEqualTo(other._id);
  }
}
